import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { IProgram, IClass, ISession, IProgramDocs } from '../../../models/IProgram';
import { IParticipant } from '../../../models/IParticipant';
import { IClassRoster, IFacilitators, IParticipants } from '../../../models/IClassRoster';
import { environment } from '../../../../environments/environment';
import { get, post, patch, put, del } from 'aws-amplify/api';
import { FILTER_TYPE, IFilterRequest } from 'src/app/shared/filter/filter.interface';
import { ApiConfigurationsService, IRequest } from 'src/app/services/shared/api-configurations.service';


@Injectable({ providedIn: 'root' })
export class ProgramService {
  private programCache = new Map<string, any>();
  private classCache = new Map<string, any>();
  private sessionCache = new Map<string, any>();
  public isSesionsModalOpen = false;
  public isProgramModalOpen = false;
  /** Set while Class Setting Mode handles browser back (blocks duplicate route guards). */
  public handlingClassSettingBrowserBack = false;

  constructor(
    private http: HttpClient,
    private apiConfigurationsService: ApiConfigurationsService
  ) { }

  public getProgramsList(): Observable<IProgram[]> {
    const getFromQueue = true;
    if (getFromQueue) {
      const body: IRequest = {
        apiConfigurations: this.apiConfigurationsService.getConfigurations('GET_PROGRAMS')
      };
      return this.apiConfigurationsService.get(body).pipe(
        catchError((error) => throwError(() => error)),
        map((data: IProgram[]) => this.sortPrograms(data) as IProgram[])
      );
    }
    const op = get({ apiName: 'portal', path: 'programs', options: {} });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((data: any) => this.sortPrograms(data) as IProgram[])
    );
  }
  private sortPrograms(programs: IProgram[]): IProgram[] {
    const data = JSON.parse(JSON.stringify(programs)).map((p: IProgram) => this.normalizeProgramConfidentiality(p));
    data.sort((a, b) => {
      const aCompare = a['name'];
      const bCompare = b['name'];
      return aCompare.localeCompare(bCompare, undefined, { numeric: true, sensitivity: 'base' });
    });
    data.sort((a, b) => {
      const aStartDate: Date = new Date(a.startDate);
      const bStartDate: Date = new Date(b.startDate);
      const aEndDate: Date = new Date(a.endDate);
      const bEndDate: Date = new Date(b.endDate);
      const endDateValue = aEndDate < bEndDate ? -1 : aEndDate > bEndDate ? 1 : 0;
      return aStartDate < bStartDate ? -1 : aStartDate > bStartDate ? 1 : endDateValue;
    });
    return data;
  }
  get(programId: any): Observable<any> {
    const op = get({ apiName: 'portal', path: `programs/${programId}`, options: {} });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((data: IProgram) => {
        const { cEventId } = data;
        const ceventRegistrationUrl = data?.programSetting?.ceventRegistrationUrl;
        const baseUrl = environment.cvent;
        const actualCventUrl = `${baseUrl}${cEventId}`;
        return {
          ...data,
          programSetting: {
            ...data['programSetting'],
            ceventRegistrationUrl: !ceventRegistrationUrl ? actualCventUrl : ceventRegistrationUrl
          }
        } as IProgram;
      })
    );
  }
  public clearClassCache(): void {
    this.classCache.clear();
  }
  // (unchanged) filterProgramsBySelectedDate(...)
  public filterProgramsBySelectedDate(programs: IProgram[], _programs: IProgram[], filterInput: IFilterRequest): IProgram[] {
    // This function is being used in multiple places. EX: programs, house and well being events
    programs = [];
    switch (filterInput.filterType) {
      case FILTER_TYPE.FROM:
        _programs.forEach((program: IProgram) => {
          let startDate: Date = new Date(program?.startDate);
          let endDate = new Date(program?.endDate);
          const today = new Date();
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);
          let filterStartDate = filterInput?.startDate;
          let filterEndDate = filterInput?.endDate;
          const tempStartDate = startDate;
          const tempFilterStartDate = filterInput?.startDate;
          if (startDate < filterStartDate && endDate >= tempFilterStartDate) {
            startDate = filterStartDate;
            filterStartDate = tempStartDate;
          }
          if (
            (startDate >= filterStartDate && startDate <= filterEndDate) ||
            (endDate >= filterStartDate && endDate <= filterEndDate)
          ) {
            programs.push(program);
          }
        });
        break;
      case FILTER_TYPE.ON:
      case FILTER_TYPE.TODAY:
        _programs.forEach((program: IProgram) => {
          const startDate: Date = new Date(program?.startDate);
          const endDate = new Date(program?.endDate);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          const filterInputStartDate: Date = new Date(filterInput?.startDate);
          const filterInputEndDate = new Date(filterInput?.endDate);
          filterInputStartDate.setHours(0, 0, 0, 0);
          filterInputEndDate.setHours(0, 0, 0, 0);
          if (filterInputStartDate >= startDate && endDate >= filterInputEndDate) {
            programs.push(program);
          }
        });
        break;
    }
    return this.programSort(programs);
  }

  getClassesByProgram(programId: string): Observable<IClass[]> {
    const op = get({ apiName: 'portal', path: `programs/${programId}/courses`, options: {} });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((data: IClass[]) => {
        data = (data || []).map((c) => ({ ...c, location: c.location || 'TBD' }));
        data.sort((a, b) => (new Date(a.startDate) > new Date(b.startDate) ? -1 : 0) * -1 ||
          a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' })
        );
        return data as IClass[];
      })
    );
  }
  getSessionsByProgram(programId: string): Observable<ISession[]> {
    const op = get({ apiName: 'portal', path: `programs/${programId}/sessions`, options: {} });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((data: any[]) => {
        data = (data || []).map((s: any) => ({ ...s, location: s.location || 'TBD' }));
        data.sort((a, b) => (new Date(a.startDate) > new Date(b.startDate) ? -1 : 0) * -1 ||
          a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' })
        );
        return data as ISession[];
      })
    );
  }
  getParticipantsByProgram(programId: string): Observable<IParticipant[]> {
    const op = get({ apiName: 'portal', path: `programs/${programId}/registrationStatus`, options: {} });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((data: any[]) => {
        (data || []).sort((a, b) =>
          a.lastName.localeCompare(b.lastName, undefined, { numeric: true, sensitivity: 'base' }) ||
          a.firstName.localeCompare(b.firstName, undefined, { numeric: true, sensitivity: 'base' })
        );
        return (data || []).map((participant) => this.normalizeParticipantPrivacy(participant));
      })
    );
  }

  public updateParticipantPrivacy(
    programId: string,
    participantIds: string | number | (string | number)[] | Set<string | number>,
    isUserPrivate: boolean
  ): Observable<void> {
    const rawIds = participantIds instanceof Set
      ? [...participantIds]
      : Array.isArray(participantIds)
        ? participantIds
        : [participantIds];
    const apiIds = rawIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));

    if (!apiIds.length) {

      return throwError(() => new Error('No valid participant IDs provided.'));

    }

    const requestBody = { participantIds: apiIds, isUserPrivate };
    const op = post({
      apiName: 'portal',
      path: `programs/${programId}/registrationStatus/privacy`,
      options: { body: JSON.parse(JSON.stringify(requestBody)) }
    });
    return from(op.response.then(async (r) => {
      try {
        return await r.body.json();
      } catch {
        return null;
      }
    })).pipe(
      map(() => undefined),
      catchError((error) => throwError(() => error))
    );
  }

  private normalizeParticipantPrivacy(participant: IParticipant): IParticipant {
    const id = participant?.participantId ?? participant?.id;
    return {
      ...participant,
      participantId: id != null ? Number(id) : participant.participantId,
      isUserPrivate: this.normalizePrivacyValue(participant?.isUserPrivate)
    };
  }

  private normalizePrivacyValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return 'false';
    }
    const normalized = String(value).trim();
    if (normalized.toUpperCase() === 'NA') {
      return 'NA';
    }
    if (normalized.toLowerCase() === 'true' || value === true) {
      return 'true';
    }
    return 'false';
  }

  public updateProgramConfidentiality(
    programIds: string | number | (string | number)[] | Set<string | number>,
    isConfidential: boolean
  ): Observable<void> {
    const rawIds = programIds instanceof Set
      ? [...programIds]
      : Array.isArray(programIds)
        ? programIds
        : [programIds];
    const apiIds = rawIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));

    if (!apiIds.length) {
      return throwError(() => new Error('No valid program IDs provided.'));
    }

    const requestBody = { programIds: apiIds, isConfidential };
    const op = post({
      apiName: 'portal',
      path: `programs/confidentiality`,
      options: { body: JSON.parse(JSON.stringify(requestBody)) }
    });
    return from(op.response.then(async (r) => {
      try {
        return await r.body.json();
      } catch {
        return null;
      }
    })).pipe(
      map(() => undefined),
      catchError((error) => throwError(() => error))
    );
  }

  public normalizeProgramConfidentiality(program: IProgram): IProgram {
    return {
      ...program,
      isConfidential: this.normalizeConfidentialityValue(program?.isConfidential)
    };
  }

  private normalizeConfidentialityValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return 'false';
    }
    const normalized = String(value).trim();
    if (normalized.toLowerCase() === 'true' || value === true) {
      return 'true';
    }
    return 'false';
  }
  patchClassesDisplayOptions(classes: IClass[]) {
    return this.http.patch('classes', classes);
  }
  patchSessionsDisplayOptions(sessions: ISession[]) {
    return this.http.patch('sessions', sessions);
  }
  patchProgramsUrlOptions(programs: IProgram) {
    return this.http.patch('programs', programs);
  }
  getClassDetail(programId: any, classId: string): Observable<IClassRoster[]> {
    const op = get({ apiName: 'portal', path: `programs/${programId}/courses/${classId}`, options: {} });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((data: IClassRoster[]) => data as IClassRoster[])
    );
  }

  getRosterParticipants(programId: string, classId: string): Observable<IParticipants[]> {
    const getFromQueue = true;
    if (getFromQueue) {
      const body: IRequest = {
        apiConfigurations: this.apiConfigurationsService.getConfigurations('GET_CLASS_ROASTER_PARTICIPANTS')
      };
      body.apiConfigurations.PATH = body.apiConfigurations.PATH
        .replace('${programId}', programId)
        .replace('${courseId}', classId);
      return this.apiConfigurationsService.get(body).pipe(
        catchError((error) => throwError(() => error)),
        map((data: IParticipants[]) => {
          data = (data || []).map((c) => ({ ...c, isEdit: false }));
          return data as IParticipants[];
        })
      );
    }
    const op = get({ apiName: 'portal', path: `programs/${programId}/courses/${classId}/participants`, options: {} });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((data: IParticipants[]) => {
        data = (data || []).map((c) => ({ ...c, isEdit: false }));
        return data as IParticipants[];
      })
    );
  }
  getRosterFacilitators(programId: string, classId: string): Observable<IFacilitators[]> {
    const op = get({
      apiName: 'portal',
      path: `programs/${programId}/courses/${classId}/facilitators`,
      options: { queryParams: { setting: 'true' } }
    });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((data: IFacilitators[]) => {
        data = (data || []).map((c) => ({ ...c, isEdit: false }));
        return data as IFacilitators[];
      })
    );
  }

  updateProgram(program: IProgram) {
    if (this.programCache.has(program.id as any)) {
      const programExits = this.programCache.get(program.id as any);
      if (program.urlTypeName === 'courseUrl') {
        programExits.courseRegistrationUrl = program.courseRegistrationUrl;
      } else {
        programExits.ceventRegistrationUrl = program.ceventRegistrationUrl;
      }
      this.programCache.delete(program.id as any);
      this.programCache.set(program.id as any, programExits);
    } else {
      this.programCache.set(program.id as any, program);
    }
  }

  saveUpdate(): Observable<any> {
    const op = patch({
      apiName: 'portal',
      path: 'programs',
      options: {
        body: JSON.parse(JSON.stringify(Array.from(this.programCache.values())))
      }
    });
    return from(op.response.then(async (r) => (await r.body) as any)).pipe(
      map((res: any) => res as any),
      catchError((error) => throwError(() => error))
    );
  }
  cancelUpdate(name: 'program' | 'class' | 'session') {
    switch (name) {
      case 'program':
        this.programCache.clear();
        break;
      case 'class':
        this.classCache.clear();
        break;
      case 'session':
        this.sessionCache.clear();
        break;
    }
  }
  isDirty(name: 'program' | 'class' | 'session') {
    switch (name) {
      case 'program':
        return this.programCache.size > 0;
      case 'class':
        return this.classCache.size > 0;
      case 'session':
        return this.sessionCache.size > 0;
    }
  }
  updateClass(classInfo: IClass) {
    if (this.classCache.has(classInfo.id as any)) {
      const classExits = this.classCache.get(classInfo.id as any);
      classExits.facilitatorAttendance = classInfo.facilitatorAttendance;
      this.classCache.delete(classInfo.id as any);
      this.classCache.set(classInfo.id as any, classExits);
    } else {
      this.classCache.set(classInfo.id as any, classInfo);
    }
  }
  updateSession(sessionInfo: ISession) {
    if (this.sessionCache.has(sessionInfo.id as any)) {
      const sessionExits = this.sessionCache.get(sessionInfo.id as any);
      sessionExits.qa = sessionInfo.qa;
      this.sessionCache.delete(sessionInfo.id as any);
      this.sessionCache.set(sessionInfo.id as any, sessionExits);
    } else {
      this.sessionCache.set(sessionInfo.id as any, sessionInfo);
    }
  }

  save(programId: string, name: 'class' | 'session') {
    const payload: any[] = Array.from(this.classCache.values());
    payload.forEach((value) => {
      if (value?.id === value?.eventId) value.id = null;
    });
    if (name === 'class') {
      const op = patch({
        apiName: 'portal',
        path: `programs/${programId}/courses`,
        options: { body: JSON.parse(JSON.stringify(payload)) }
      });
      return from(op.response.then(async (r) => (await r.body) as any)).pipe(
        map(() => { }),
        catchError((error) => throwError(() => error))
      );
    }
    const op = patch({
      apiName: 'portal',
      path: `programs/${programId}/sessions`,
      options: { body: JSON.parse(JSON.stringify(payload)) }
    });
    return from(op.response.then(async (r) => (await r.body) as any)).pipe(
      map(() => { }),
      catchError((error) => throwError(() => error))
    );
  }
  programSort(programs: any[]): any[] {
    return programs.sort((a, b) => {
      // Helper: safe numeric time for sorting (null/invalid -> 0)
      const toTime = (d: any) => {
        const t = new Date(d as any).getTime();
        return Number.isFinite(t) ? t : 0;
      };
      // Helper: safe string compare
      const cmpStr = (x: any, y: any) =>
        String(x ?? '').localeCompare(String(y ?? ''), undefined, { sensitivity: 'base' });
      // Keep original logic: choose rule based on existence of title, else based on eventType
      if (a?.hasOwnProperty('title')) {
        return (
          toTime(a?.startDate) - toTime(b?.startDate) ||
          toTime(a?.endDate) - toTime(b?.endDate) ||
          cmpStr(a?.title, b?.title)
        );
      }
      // FIX: ('1' || '4') was always '1'. Use includes.
      if (['1', '4'].includes(a?.eventType)) {
        return (
          toTime(a?.startDate) - toTime(b?.startDate) ||
          toTime(a?.endDate) - toTime(b?.endDate) ||
          cmpStr(a?.eventDetailsName, b?.eventDetailsName)
        );
      }
      // Date-only sort (midnight) + name tiebreaker
      const aStartDay = new Date(a?.startDate);
      aStartDay.setHours(0, 0, 0, 0);
      const bStartDay = new Date(b?.startDate);
      bStartDay.setHours(0, 0, 0, 0);
      const aEndDay = new Date(a?.endDate);
      aEndDay.setHours(0, 0, 0, 0);
      const bEndDay = new Date(b?.endDate);
      bEndDay.setHours(0, 0, 0, 0);
      return (
        aStartDay.getTime() - bStartDay.getTime() ||
        aEndDay.getTime() - bEndDay.getTime() ||
        cmpStr(a?.name, b?.name)
      );
    });
  }

  public getProgramDocuments(programId: any): Observable<IProgramDocs[]> {
    const op = get({
      apiName: 'portal',
      path: `programs/documents`,
      options: { queryParams: { programId: String(programId) } }
    });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((data: IProgramDocs[]) => {
        data = (data || []).map((s: IProgramDocs) => ({ ...s, isEditable: false, readMore: false }));
        return data as IProgramDocs[];
      })
    );
  }

  public addProgramDocuments(programId: any, data: IProgramDocs): Observable<IProgramDocs> {
    data.url = data?.url?.trim();
    const op = post({
      apiName: 'portal',
      path: `programs/documents`,
      options: {
        queryParams: { programId: String(programId) },
        body: JSON.parse(JSON.stringify(data))
      }
    });
    return from(op.response.then(async (r) => (await r.body) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((res: any) => res as any)
    );
  }
  public deleteProgramDocument(programId: string, docId: string): Observable<IProgramDocs> {
    const op = del({
      apiName: 'portal',
      path: `programs/documents`,
      options: { queryParams: { programId, documentId: docId } }
    });
    return from(op.response.then(async (r) => (await r.body) as any)).pipe(
      map((res: any) => res as any),
      catchError((error) => throwError(() => error))
    );
  }
  public updateDocument(programId: string, docId: string, data: IProgramDocs): Observable<IProgramDocs> {
    data.url = data?.url?.trim();
    const op = put({
      apiName: 'portal',
      path: `programs/documents`,
      options: {
        queryParams: { programId, documentId: docId },
        body: JSON.parse(JSON.stringify(data))
      }
    });
    return from(op.response.then(async (r) => (await r.body) as any)).pipe(
      map((res: any) => res as any),
      catchError((error) => throwError(() => error))
    );
  }
  public deleteSessions(programId: string, sessionIds: any): Observable<any[]> {
    const requestBody = {
      sessionIds: sessionIds instanceof Set ? [...sessionIds] : [sessionIds]
    };
    const op = del({
      apiName: 'portal',
      path: `programs/${programId}/sessions`,
      options: { body: JSON.parse(JSON.stringify(requestBody)) }
    });
    return from(op.response.then(async (r) => (await r.body) as any)).pipe(
      map((res: any) => res as any),
      catchError((error) => throwError(() => error))
    );
  }
}
